import { frontDeskMay, isFrontDesk, moduleOn } from "@teamna/shared";
import { useState } from "react";
import { Button, EmptyState, FormSection, PlusCircleIcon } from "../components/index.ts";
import { OfficePage, runsBusiness } from "./office.tsx";
import { useActiveBusiness, useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { AskStaffCard } from "./ask-staff.tsx";
import { CoverRuleRow } from "../covers/cover-rule.tsx";
import { AddAnywayRuleRow } from "./add-anyway-rule.tsx";
import { TeamManager } from "./setup/team.tsx";

/**
 * The Staff page (web-app-v2.md slice E; "Team" until the owner menu revamp of 2026-10-09, where
 * Team became the menu group). Redesigned on 2026-11-03: the people come first, and each row opens
 * the person's page. "Add a person" (header) opens a side panel. Under the list: the ask card of the
 * module "Coaches add their swimmers" when it is on, then one Rules block. The same manager as
 * setup step 2, without the step's "Done", because after the first day the team is something an
 * owner changes rather than finishes.
 *
 * The front desk opens it when the owner turns on "Team" in Settings, Front desk: the list only,
 * with the mobile numbers, and no change (`TeamManager` with `readOnly`).
 */
/** The page of each person is a Desk page; Control Center has no route for it, so it turns it off (`memberPage`). */

export function OwnerTeamScreen({ memberPage = true }: { memberPage?: boolean }) {
  const [adding, setAdding] = useState(false);
  return (
    <OfficePage
      title="Staff"
      description="Who runs the business with you, and what each person can do."
      actions={<TeamActions onAdd={() => setAdding(true)} />}
    >
      {(business) =>
        runsBusiness(business) ? (
          <div className="flex flex-col gap-8">
            <TeamManager businessId={business.businessId} memberLink={memberPage} addPanel={{ open: adding, onClose: () => setAdding(false) }} />
            <AskStaffWhenOn businessId={business.businessId} />
            <FormSection title="Rules" description="How your team works with covers and with people who have no package.">
              <CoverRuleRow businessId={business.businessId} />
              <AddAnywayRuleRow businessId={business.businessId} />
            </FormSection>
          </div>
        ) : isFrontDesk(business.staffRole) && frontDeskMay(business.frontDesk, "team") ? (
          <TeamManager businessId={business.businessId} readOnly />
        ) : (
          <EmptyState title="Owner and admin only" message="Ask the owner to change the team." />
        )
      }
    </OfficePage>
  );
}

/** The ask card, only when the module "Coaches add their swimmers" is on (Settings, Modules). */
function AskStaffWhenOn({ businessId }: { businessId: string }) {
  const business = useOwnerBusiness(businessId);
  return moduleOn(business.data?.settings, "coaches_add_participants") ? <AskStaffCard businessId={businessId} /> : null;
}

/**
 * The header has one button, "Add a person" (feedback 2026-11-03). Team photo is in the menu
 * (Team, Team photo) and in Tools; the WhatsApp sync is on the Parents page and in setup.
 */
function TeamActions({ onAdd }: { onAdd: () => void }) {
  const { business } = useActiveBusiness();
  if (!business || !runsBusiness(business)) return null;
  return (
    <Button onClick={onAdd}>
      <PlusCircleIcon size={17} />
      Add a person
    </Button>
  );
}
